import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { refreshPins } from "../utils/refresh_pins"

// The "Edit pin" sheet on the arrange editor, opened by a card's ⋯ → Edit
// (pin_menu dispatches pin-menu:edit with the pin's current values, so the
// sheet fills in without a request). Saving PATCHes /pins/:id and reloads, the
// same way adding one does — the card's title, host and icon all come from the
// server, and re-rendering beats patching four nodes by hand.
export default class extends Controller {
    static targets = ["modal", "url", "title", "favicon", "emoji", "error"]

    open(event) {
        const { url, linkUrl, title, favicon, emoji } = event.detail || {}
        this._url = url
        this.urlTarget.value = linkUrl || ""
        this.titleTarget.value = title || ""
        // Only a custom icon belongs in the field: the derived one is what you
        // get by leaving it empty, so showing it would turn a default into
        // something the user appears to have chosen.
        this.faviconTarget.value = this._customFavicon(favicon)
        if (this.hasEmojiTarget) this.emojiTarget.value = emoji || ""
        this._clearError()
        this.modalTarget.classList.remove("hidden")
        requestAnimationFrame(() => this.titleTarget.focus())
        this._boundKeydown = this._boundKeydown || ((e) => { if (e.key === "Escape") this.close() })
        document.addEventListener("keydown", this._boundKeydown)
    }

    close(event) {
        event?.preventDefault()
        this.modalTarget.classList.add("hidden")
        if (this._boundKeydown) document.removeEventListener("keydown", this._boundKeydown)
    }

    backdropClose(event) {
        if (event.target === this.modalTarget) this.close()
    }

    save(event) {
        event?.preventDefault()
        if (!this._url) return
        const url = this.urlTarget.value.trim()
        if (!url) {
            this._showError("Enter a website URL")
            return
        }
        const trigger = event?.currentTarget
        if (trigger) trigger.disabled = true
        this._clearError()
        fetch(this._url, {
            method: "PATCH",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ metadata: { url, title: this.titleTarget.value.trim(), favicon: this.faviconTarget.value.trim(), ...(this.hasEmojiTarget ? { emoji: this.emojiTarget.value.trim() } : {}) } })
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not save this pin")
                refreshPins(this.element)
            })
            .catch((err) => {
                this._showError(err.message || "Could not save this pin")
                if (trigger) trigger.disabled = false
            })
    }

    // The s2 URL we derive from the host is not something the user typed, so it
    // never populates the field — leaving it empty is what asks for it.
    _customFavicon(favicon) {
        if (!favicon) return ""
        try {
            return new URL(favicon).hostname === "www.google.com" ? "" : favicon
        } catch {
            return ""
        }
    }

    _showError(message) {
        this.errorTarget.textContent = message
        this.errorTarget.classList.remove("hidden")
    }

    _clearError() {
        this.errorTarget.textContent = ""
        this.errorTarget.classList.add("hidden")
    }
}
